import { useState, useEffect } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { fadeUp } from "@/lib/motion";

export default function Login() {
  const { user, login, loginTotp, checking } = useAuth();
  const [searchParams] = useSearchParams();
  const [gate, setGate] = useState("checking"); // checking | ok | denied
  const [step, setStep] = useState("password"); // password | totp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  // Secret gate: the sign-in page is invisible unless opened with the correct
  // key (/admin/login?key=…). Wrong/absent key → looks like a 404.
  useEffect(() => {
    const key = searchParams.get("key") || "";
    api
      .post("/auth/login-gate", { key })
      .then(() => setGate("ok"))
      .catch(() => setGate("denied"));
  }, [searchParams]);

  if (!checking && user) return <Navigate to="/admin" replace />;

  if (gate === "checking") {
    return <div className="min-h-screen bg-[#FAFAFA]" data-testid="login-gate-checking" />;
  }

  if (gate === "denied") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6" data-testid="login-notfound">
        <div className="text-center">
          <p className="font-serif text-5xl text-[#1A1F24]">404</p>
          <p className="mt-4 text-sm tracking-[0.22em] uppercase text-[#5C6670]">Page not found</p>
          <Link to="/" className="mt-8 inline-block gv-link text-sm tracking-[0.2em] uppercase text-[#C5A059]">
            ← Back to the site
          </Link>
        </div>
      </div>
    );
  }

  const submitPassword = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await login(email, password);
      if (data.requires_totp) {
        setChallengeId(data.challenge_id);
        setStep("totp");
      } else {
        navigate("/admin");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not sign in."));
    } finally {
      setSubmitting(false);
    }
  };

  const submitTotp = async (e) => {
    e.preventDefault();
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6) {
      toast.error("Enter the 6-digit code from your authenticator app.");
      return;
    }
    setSubmitting(true);
    try {
      await loginTotp(challengeId, digits);
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Invalid or expired code."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6">
      <Toaster position="top-center" />
      {step === "password" ? (
        <motion.form
          onSubmit={submitPassword}
          {...fadeUp}
          data-testid="login-form"
          className="w-full max-w-md bg-white border border-[#E5E5E5] p-10 md:p-14"
        >
          <Link to="/" className="font-serif text-2xl text-[#1A1F24]">Gemma <span className="italic text-[#C5A059]">Veda</span></Link>
          <h1 className="font-serif text-3xl mt-6 text-[#1A1F24]">Studio sign-in</h1>
          <p className="mt-2 text-sm text-[#5C6670]">Manage books, journal & letters.</p>
          <div className="mt-8 space-y-6">
            <div>
              <label className="gv-label">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="gv-input mt-2" data-testid="login-email" />
            </div>
            <div>
              <label className="gv-label">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="gv-input mt-2" data-testid="login-password" />
            </div>
          </div>
          <button type="submit" disabled={submitting} className="gv-btn-gold mt-10 w-full justify-center" data-testid="login-submit">
            {submitting ? "Signing in…" : "Continue"}
          </button>
          <div className="mt-6 flex items-center justify-between">
            <Link to="/admin/forgot-password" className="text-xs tracking-[0.22em] uppercase text-[#C5A059] hover:text-[#A68545]" data-testid="login-forgot">
              Forgot password?
            </Link>
            <Link to="/" className="text-xs tracking-[0.22em] uppercase text-[#5C6670] hover:text-[#C5A059]">← Back to the site</Link>
          </div>
        </motion.form>
      ) : (
        <motion.form
          onSubmit={submitTotp}
          {...fadeUp}
          data-testid="login-totp-form"
          className="w-full max-w-md bg-white border border-[#E5E5E5] p-10 md:p-14"
        >
          <h1 className="font-serif text-3xl text-[#1A1F24]">Two-factor code</h1>
          <p className="mt-2 text-sm text-[#5C6670]">Enter the 6-digit code from your authenticator app.</p>
          <input
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoComplete="one-time-code"
            autoFocus
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="gv-input mt-8 text-center text-2xl tracking-[0.5em]"
            data-testid="login-totp-code"
            placeholder="••••••"
          />
          <button type="submit" disabled={submitting} className="gv-btn-gold mt-8 w-full justify-center" data-testid="login-totp-submit">
            {submitting ? "Verifying…" : "Sign in"}
          </button>
          <button
            type="button"
            onClick={() => { setStep("password"); setCode(""); }}
            className="mt-6 w-full text-center text-xs tracking-[0.22em] uppercase text-[#5C6670] hover:text-[#C5A059]"
            data-testid="login-totp-back"
          >
            ← Start over
          </button>
        </motion.form>
      )}
    </div>
  );
}
